import { api } from '@/lib/api';
import { isConnectionError } from '@/lib/connectionErrors';
import { optionalRequestId } from '@/lib/requestId';
import { radarAnalytics, type RadarProperties } from './radarAnalytics';

const safeCodes = new Set([
  'over_email_send_rate_limit', 'over_request_rate_limit', 'otp_expired', 'otp_disabled',
  'email_address_invalid', 'email_address_not_authorized', 'email_provider_disabled',
  'signup_disabled', 'captcha_failed', 'unexpected_failure', 'validation_failed',
  'hook_timeout', 'hook_timeout_after_retry', 'user_banned', 'email_not_confirmed',
  'invalid_credentials', 'no_session',
]);

export function authErrorDetails(error: unknown): Pick<RadarProperties, 'error_code' | 'status'> {
  const value = error as { code?: string; name?: string; status?: number; message?: string } | null;
  const status = typeof value?.status === 'number' && Number.isInteger(value.status)
    && value.status >= 100 && value.status <= 599 ? value.status : undefined;
  // Supabase can wrap fetch failures in AuthRetryableFetchError (status 0).
  // An arbitrary TypeError may be a programming bug, not a transport failure.
  const network = isConnectionError(error);
  return { status, error_code: safeCodes.has(value?.code || '') ? value!.code
    : network ? 'network_error' : 'unknown_error' };
}

function report(email: string, properties: RadarProperties) {
  try { radarAnalytics.capture(properties.outcome === 'started' ? 'radar_auth_started' : 'radar_auth_result', properties); }
  catch { /* Diagnostics never gates authentication. */ }
  // First-party collector is independent of the optional analytics SDK.
  // Never include an OTP, session, provider message or user profile.
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 2000);
  const { auth_method: _method, ...payload } = properties;
  try {
    void fetch(`${api.baseUrl}/api/radar/auth/diagnostics`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'omit',
      body: JSON.stringify({ ...payload, email }), signal: controller.signal,
    }).catch(() => {}).finally(() => window.clearTimeout(timeout));
  } catch { window.clearTimeout(timeout); }
}

export function beginAuthDiagnostic(email: string, flowId: string, stage: 'send' | 'verify', onSlow: () => void) {
  const started = performance.now();
  const requestId = optionalRequestId();
  const base: RadarProperties = { auth_method: 'email', auth_flow_id: flowId, auth_stage: stage,
    request_id: requestId, delivery_status: 'unknown' };
  function emit(outcome: RadarProperties['outcome'], extra: RadarProperties = {}) {
    // The collector requires valid UUIDs. Observation and login remain usable
    // even when the runtime cannot provide secure correlation identifiers.
    if (!flowId || !requestId) return;
    report(email, { ...base, ...extra, outcome,
      elapsed_ms: Math.min(86_400_000, Math.max(0, Math.round(performance.now() - started))) });
  }
  emit('started');
  let finished = false;
  const timer = window.setTimeout(() => {
    if (finished) return;
    emit('unknown', { error_code: 'request_timeout' });
    onSlow();
  }, 15_000);
  return {
    cancelObservation() { window.clearTimeout(timer); },
    finish(outcome: 'code_sent' | 'verified' | 'failed', error?: unknown) {
      if (finished) return;
      finished = true;
      window.clearTimeout(timer);
      emit(outcome, outcome === 'failed' ? authErrorDetails(error) : {});
    },
  };
}
